import {
  CustomerPrepaidDisplayStatus,
  CustomerPrepaidPurchase,
  CustomerPrepaidPurchaser,
  CustomerPrepaidRedemption,
  CustomerPrepaidWash,
} from '../../services/customer-prepaid.service';
import { cardLabel } from './payment-method.copy';
import { vendorLookupLabel } from './vendor-membership-lookup.util';
import type { CancelRefundDone } from '../components/cancel-refund-panel/cancel-refund-panel.component';

/**
 * CWS-1833: display rules for the Customers → Prepaid cards. Pure, so the
 * component only wires them to the template.
 */

const STATUS_LABELS: Readonly<Record<CustomerPrepaidDisplayStatus, string>> = Object.freeze({
  // CWS-1883: a live book is "Scheduled" — bought and waiting to be redeemed. The server
  // value stays `active` because `is_live` and `can_cancel` are derived from it.
  active: 'Scheduled',
  completed: 'Completed',
  expired: 'Expired',
  cancelled: 'Cancelled',
  unpaid: 'Unpaid',
});

/** `orders.source` on a vendor-imported row names the vendor ("DRB Membership"). */
const VENDOR_SOURCE = /\b(drb|washify|everwash|iwashpos)\b/i;

/** The status pill: "Scheduled", "Completed", "Cancelled", "Expired"; anything unknown reads Expired. */
export function prepaidStatusLabel(status: string | null | undefined): string {
  return STATUS_LABELS[(status ?? '') as CustomerPrepaidDisplayStatus] ?? STATUS_LABELS.expired;
}

/** "2 of 5 left"; "—" for a book with no washes on it. */
export function prepaidWashesLeftLabel(
  purchase: Pick<CustomerPrepaidPurchase, 'remaining_washes' | 'total_washes'>
): string {
  const total = Math.max(0, Number(purchase.total_washes) || 0);
  if (total === 0) {
    return '—';
  }
  const remaining = Math.min(total, Math.max(0, Number(purchase.remaining_washes) || 0));
  return `${remaining} of ${total} left`;
}

/** "Champion × 5, Tire Shine × 4"; "—" when the book carries no items. */
export function prepaidWashNamesLabel(washes: CustomerPrepaidWash[] | null | undefined): string {
  const parts = (washes ?? [])
    .map(wash => {
      const name = String(wash.name ?? wash.wash_type ?? '').trim();
      if (!name) {
        return '';
      }
      const limit = Math.max(0, Number(wash.wash_limit) || 0);
      return limit > 0 ? `${name} × ${limit}` : name;
    })
    .filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}

/**
 * The chip on the card: the first wash with its count, and an ellipsis when the book
 * carries more lines ("Champion × 5…"); the full list goes in the tooltip.
 */
export function prepaidWashNamesBadge(washes: CustomerPrepaidWash[] | null | undefined): string {
  const parts = prepaidWashNamesLabel(washes).split(', ');
  if (parts.length === 0 || parts[0] === '—') {
    return '—';
  }
  return parts.length > 1 ? `${parts[0]}…` : parts[0];
}

/** "Visa •••• 4242" from the purchase charge's snapshot; "—" for cash or no snapshot. */
export function prepaidCardOnFileLabel(
  card: CustomerPrepaidPurchase['card_on_file'] | null | undefined
): string {
  if (!card?.last4) {
    return '—';
  }
  return cardLabel({ brand: card.brand ?? 'card', last4: card.last4 });
}

/**
 * Where the book was sold: "B2C app" / "Website" for a Way-app sale, the vendor for an
 * import, "Lane" for a sale made at the gate, else "Shop".
 */
export function prepaidSourceLabel(
  purchase: Pick<CustomerPrepaidPurchase, 'source' | 'order_source'>
): string {
  const source = String(purchase.source ?? '')
    .trim()
    .toLowerCase();
  if (source === 'way_app') {
    return 'B2C app';
  }
  if (source === 'way_web') {
    return 'Website';
  }
  const vendor = VENDOR_SOURCE.exec(source);
  if (vendor) {
    return vendorLookupLabel(vendor[1]);
  }
  return String(purchase.order_source ?? '')
    .trim()
    .toLowerCase() === 'lpr'
    ? 'Lane'
    : 'Shop';
}

/** "Porsche 911" beside the plate chip, as the design shows the car; empty when not known. */
export function prepaidVehicleLabel(
  purchase: Pick<CustomerPrepaidPurchase, 'vehicle_make' | 'vehicle_model'>
): string {
  return [purchase.vehicle_make, purchase.vehicle_model]
    .map(part => String(part ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

/** Live books first, in the order given; the rest are history. */
export function splitPrepaidPurchases<T extends Pick<CustomerPrepaidPurchase, 'is_live'>>(
  purchases: T[] | null | undefined
): { live: T[]; past: T[] } {
  const list = purchases ?? [];
  return { live: list.filter(p => p.is_live), past: list.filter(p => !p.is_live) };
}

/**
 * Cancel prepaid is offered on a live Way book (`can_cancel`, server-derived) when the tenant
 * switched cancel & refund on at this location. No role gate, as with memberships (CWS-1776).
 */
export function canCancelPrepaid(
  purchase: Pick<CustomerPrepaidPurchase, 'can_cancel'>,
  cancelAvailable: boolean
): boolean {
  return purchase.can_cancel === true && cancelAvailable;
}

/** What the notice above the refreshed cards says once the panel reports back. */
export function prepaidCancelNotice(done: CancelRefundDone): { title: string; body: string } {
  return {
    title: done.result.status === 'failed' ? 'Prepaid not cancelled' : 'Prepaid cancelled',
    body: done.summary,
  };
}

/* ---------------------------------------------------------------------------------------
 * CWS-1881: "Purchased by" and the redemption log. Pure, so the card only wires them.
 * ------------------------------------------------------------------------------------- */

/** A value for the purchaser block or a log cell: the text, and whether it is a placeholder. */
export interface PrepaidDisplayValue {
  text: string;
  /** True when nothing was given, so the cell renders muted rather than as a real value. */
  muted: boolean;
}

const NOT_GIVEN = '—';

function displayValue(value: string | null | undefined): PrepaidDisplayValue {
  const text = String(value ?? '').trim();
  return text ? { text, muted: false } : { text: NOT_GIVEN, muted: true };
}

/** The four "Purchased by" cells. A book sold with a phone only shows "—" for the email. */
export function prepaidPurchaserCells(purchaser: CustomerPrepaidPurchaser | null | undefined): {
  name: PrepaidDisplayValue;
  phone: PrepaidDisplayValue;
  email: PrepaidDisplayValue;
  plate: PrepaidDisplayValue;
} {
  return {
    name: displayValue(purchaser?.name),
    phone: displayValue(purchaser?.phone),
    email: displayValue(purchaser?.email),
    plate: displayValue(purchaser?.plate),
  };
}

/** One rendered row of the redemption log. */
export interface PrepaidRedemptionRow {
  id: string;
  /** "Oct 05, 2026" and "9:14 AM", stacked in one cell as the design shows. */
  date: string;
  time: string;
  plate: PrepaidDisplayValue;
  name: PrepaidDisplayValue;
  phone: PrepaidDisplayValue;
  email: PrepaidDisplayValue;
  /** Shown under the name; which wash this visit used, when the book has more than one kind. */
  note: string;
  /**
   * The row's "view customer" link target. Null when no customer record exists for the
   * redeemer — the design's rule is that only a shared phone creates one, so a name-and-
   * email-only row, and an anonymous walk-in, have no link.
   */
  customerId: string | null;
}

function formatLogDate(iso: string, timeZone?: string): { date: string; time: string } {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) {
    return { date: NOT_GIVEN, time: '' };
  }
  const tz = timeZone ? { timeZone } : {};
  return {
    date: at.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      ...tz,
    }),
    time: at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...tz }),
  };
}

/**
 * The log rows, newest first as the API returns them.
 *
 * The wash name is only worth a line when the book has more than one kind of wash — on a
 * "5 × Champion" book every row would say Champion, which is noise.
 */
export function prepaidRedemptionRows(
  redemptions: CustomerPrepaidRedemption[] | null | undefined,
  options: { timeZone?: string; showWashName?: boolean } = {}
): PrepaidRedemptionRow[] {
  return (redemptions ?? []).map(redemption => {
    const { date, time } = formatLogDate(redemption.redeemed_at, options.timeZone);
    return {
      id: redemption.id,
      date,
      time,
      plate: displayValue(redemption.plate),
      name: displayValue(redemption.name),
      phone: displayValue(redemption.phone),
      email: displayValue(redemption.email),
      note: options.showWashName ? String(redemption.wash_name ?? '').trim() : '',
      customerId: String(redemption.customer_id ?? '').trim() || null,
    };
  });
}

/** True when the book carries more than one kind of wash, so the log names which was used. */
export function prepaidBookHasSeveralWashKinds(
  washes: CustomerPrepaidWash[] | null | undefined
): boolean {
  const names = new Set(
    (washes ?? []).map(w => String(w.name ?? w.wash_type ?? '').trim()).filter(Boolean)
  );
  return names.size > 1;
}

/** "Redemption history · 4"; the count the card already has, so it reads right before the log loads. */
export function prepaidRedemptionLogTitle(redeemedWashes: number | null | undefined): string {
  const count = Math.max(0, Number(redeemedWashes) || 0);
  return `Redemption history · ${count}`;
}

/** Verbatim from the design; the one line that explains why some rows have no link. */
export const PREPAID_REDEMPTION_LOG_HELP =
  'A customer record is created only when a phone number is shared. Use the link icon to open and edit that customer.';
